import { useCallback, useRef, useState } from 'react';

/**
 * Hook to allow components that can be controlled and uncontrolled to get and set state.
 *
 * Inspired by MUI's neat controlled util
 * https://github.com/mui-org/material-ui/blob/master/packages/mui-utils/src/useControlled.js
 */
export default function useControlled<T>({
  controlledValue,
  default: defaultProp,
}: {
  controlledValue?: T;
  default: T;
}) {
  // eslint-disable-next-line @persona-id/eqeq-null-undefined
  const { current: isControlled } = useRef(controlledValue !== undefined);
  const [valueState, setValue] = useState<T | undefined>(defaultProp);
  const value = isControlled ? controlledValue : valueState;

  const setValueIfUncontrolled = useCallback((newValue: T | undefined) => {
    if (!isControlled) {
      setValue(newValue);
    }
  }, []);

  return [value, setValueIfUncontrolled] as const;
}
